import React, { useMemo } from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { projectCashApis } from "../../../api/ProjectCash";
import { fmtINR } from "../reports/ThreeMConsolidation/threeMShared";
import {
  PROJECT_CASH_TEMPLATE_HEADERS,
  PROJECT_CASH_TEMPLATE_COL_WIDTHS,
  PROJECT_CASH_TEMPLATE_SAMPLE_ROWS,
  PROJECT_CASH_TEMPLATE_INSTRUCTIONS,
} from "./projectCashTemplate";
import { cashHeadMeta, fmtCashDate, fmtCashMonth } from "./projectCashShared";

// Thin wrapper over the shared ExcelUploadStageModal (upload -> validating ->
// preview -> committing -> done | failed). Backend job type:
// 'project-cash-entries-upload'. Rows are keyed on (head, month, referenceNo):
// a matching row is updated, otherwise created. Nothing is ever deleted.

const generateProjectCashTemplate = () => {
  const headers = [...PROJECT_CASH_TEMPLATE_HEADERS];
  const sampleRows = PROJECT_CASH_TEMPLATE_SAMPLE_ROWS.map((row) => [...row]);
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [...PROJECT_CASH_TEMPLATE_COL_WIDTHS];

  const instructionSheet = XLSX.utils.aoa_to_sheet(PROJECT_CASH_TEMPLATE_INSTRUCTIONS);
  instructionSheet["!cols"] = [{ wch: 18 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Cash Entries");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "project-cash-entries-upload-template.xlsx");
};

const dash = (v: any) => (v === null || v === undefined || v === "" ? "—" : v);

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 90,
    fixed: "left",
    render: (_: any, record: any) =>
      record?.Error ? (
        <Popover
          title="Row errors"
          content={
            <ul style={{ margin: 0, paddingLeft: 16, maxWidth: 420 }}>
              {(Array.isArray(record.Error) ? record.Error : [record.Error]).map(
                (err: string, i: number) => (
                  <li key={i}>{err}</li>
                )
              )}
            </ul>
          }
        >
          <Tag color="red" style={{ cursor: "pointer" }}>
            Error
          </Tag>
        </Popover>
      ) : (
        <Tag color="green">Valid</Tag>
      ),
  },
  {
    title: "Action",
    dataIndex: "__action",
    key: "__action",
    width: 110,
    render: (action: string) =>
      action === "New" ? (
        <Tag color="green">New</Tag>
      ) : action === "Update" ? (
        <Tag color="gold">Update</Tag>
      ) : action ? (
        <Tag>{action}</Tag>
      ) : (
        "—"
      ),
  },
  { title: "Excel Row", dataIndex: "excelRow", key: "excelRow", width: 90 },
  {
    title: "Head",
    dataIndex: "head",
    key: "head",
    width: 130,
    render: (head: string) => cashHeadMeta(head)?.label || dash(head),
  },
  {
    title: "Month",
    dataIndex: "month",
    key: "month",
    width: 110,
    render: (month: string) => (month ? fmtCashMonth(month) : "—"),
  },
  {
    title: "Reference No",
    dataIndex: "referenceNo",
    key: "referenceNo",
    width: 140,
    render: dash,
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    width: 120,
    render: (date: string) => (date ? fmtCashDate(date) : "—"),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 150,
    align: "right",
    render: (value: any, record: any) =>
      record?.Error || value === null || value === undefined || value === "" ? (
        dash(value)
      ) : (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(value)}</span>
      ),
  },
  { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 240, render: dash },
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const newRows = rows.filter((row) => !row?.Error && row?.__action === "New");
  const updateRows = rows.filter((row) => !row?.Error && row?.__action === "Update");
  const unchangedRows = rows.filter(
    (row) => !row?.Error && row?.__action === "No Change"
  );
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "new", label: `New (${newRows.length})`, rows: newRows },
    { key: "update", label: `Update (${updateRows.length})`, rows: updateRows },
    ...(unchangedRows.length
      ? [
          {
            key: "unchanged",
            label: `No Change (${unchangedRows.length})`,
            rows: unchangedRows,
          },
        ]
      : []),
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const projectCashUploadConfig: ExcelUploadTypeConfig = {
  title: "Bulk Project Cash Entries Upload",
  entityLabel: "Project Cash Entries",
  templateSource: { kind: "client", generate: generateProjectCashTemplate },
  startUpload: (formData) => projectCashApis.uploadExcel(formData),
  // Overridden per-instance below to carry the active projectId.
  buildBody: (comments) => ({ comment: comments ? comments : "N/A" }),
  previewColumns,
  previewScrollX: 1200,
  bucketRows,
  rowErrorLabel: (row) =>
    row.excelRow ? `Row ${row.excelRow}` : cashHeadMeta(row.head)?.label || "Row",
  copy: {
    uploadIntro: (
      <p>
        You're about to import Project Cash Entries — one row per amount, with
        its Head (RA Bill, Escalation, Site Expenses or Salaries) and Month.
        Head + Month + Reference No identifies an entry: a row that matches an
        existing entry <strong>updates it</strong>, anything else is added.
        Entries missing from the file are <strong>never deleted</strong>.
      </p>
    ),
    validatingHeading: "Validating your file…",
    validatingSub:
      "Checking heads, months and amounts and matching rows to existing entries. This normally takes a few seconds.",
    committingHeading: "Saving cash entries…",
    committingSub: "Adding and updating entries. This normally takes a few seconds.",
    commitCta: "Save Entries",
    doneHeading: "Cash entries saved successfully",
    doneSub: (report: UploadReportState) => (
      <>{report.successCount} entr(ies) added or updated.</>
    ),
    creationFailedNote:
      "Save failed — no entries were written (the batch is all-or-nothing).",
  },
  discardOnCancelFromPreview: false,
};

const ProjectCashUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
  projectName,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number;
  projectName?: string;
}) => {
  const config = useMemo<ExcelUploadTypeConfig>(
    () => ({
      ...projectCashUploadConfig,
      buildBody: (comments: string) => ({
        comment: comments ? comments : "N/A",
        projectId: String(projectId),
      }),
      introSection: projectName ? (
        <div className="mt-3 text-soft" style={{ fontSize: 13 }}>
          Entries will be saved to <strong>{projectName}</strong>.
        </div>
      ) : undefined,
    }),
    [projectId, projectName]
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default ProjectCashUploadModal;
